import { action, makeObservable, observable, runInAction } from 'mobx';
import isNull from 'lodash/isNull';

import { injector } from 'Injector';
import { User, UserType } from 'user/User';
import { Teacher } from 'user/teacher/Teacher';

import { USER_SERVICE, UserService } from 'user/UserService';
import { STORAGE_INTERACTOR_KEY, StorageInteractor } from 'utils/storageInteractor';
import { ArticleService } from 'assignment/service';
import { ARTICLE_SERVICE_KEY } from 'assignment/Assignment';
import { Locales, ReturnUrl } from 'utils/enums';
import { messageOf } from 'utils/apiError';


/**
 * Feide authorization codes already exchanged, by code. See getUserWithToken.
 */
const codeExchanges = new Map<string, Promise<void>>();

export class LoginStore {
  private userService: UserService = injector.get<UserService>(USER_SERVICE);
  private storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);
  private wpService: ArticleService = injector.get(ARTICLE_SERVICE_KEY);
  @observable public currentUser: User | null = null;
  @observable public isCurrentUserFetching: boolean = false;

  constructor() {
    if (this.storageInteractor.getUser()) {
      this.currentUser = this.storageInteractor.getUser();
    }

    makeObservable(this);
  }

  /**
   * Every write here goes through runInAction.
   *
   * `enforceActions: 'observed'` is on, and an async method is not an action: `@action` would
   * only cover up to the first `await`, and this one had none at all. The writes below are
   * observed — `isCurrentUserFetching` gates the login spinner — so each produced a console
   * warning and, more to the point, a mutation mobx could not batch.
   */
  private async exchangeCode(code: string) {
    runInAction(() => {
      this.isCurrentUserFetching = true;
    });

    /**
     * A failed exchange must still clear `isCurrentUserFetching`.
     *
     * The login form shows a loader while it is true, and a logged-out user is sent there while
     * this runs. The flag was cleared only on the last line below, so when the request rejected
     * — a network error, a 500 — it stayed true, and the login page showed a spinner that never
     * stopped, with nothing to say why and nothing to press. Clearing it here puts the form back.
     *
     * An invalid code was never affected: it resolves `null` and reaches the last line.
     */
    let response: string | null;
    try {
      response = await this.userService.getUserWithToken(code);
    } catch (error) {
      console.error('Could not exchange the Feide code for a user:', messageOf(error));
      runInAction(() => { this.isCurrentUserFetching = false; });

      return;
    }

    if (response && !isNull(response)) {
      // Validate return url;
      const sReturnUrl = window.localStorage.getItem(ReturnUrl.RETURN_URL);

      if (sReturnUrl !== null) {
        const dDateNow = new Date().valueOf();
        const dDateExpiredUrl = window.localStorage.getItem(ReturnUrl.TIME_EXPIRED);
        const diffExpiredNow = BigInt(dDateNow) - BigInt(dDateExpiredUrl!);
        const maxMinutesExprired: number = 300000;

        window.localStorage.removeItem(ReturnUrl.RETURN_URL);
        window.localStorage.removeItem(ReturnUrl.TIME_EXPIRED);

        if (diffExpiredNow < maxMinutesExprired) {
          window.location.href = sReturnUrl!;
          return;
        }
      }

      runInAction(() => {
        this.currentUser = this.storageInteractor.getUser();
      });
    }

    runInAction(() => {
      this.isCurrentUserFetching = false;
    });
  }

  /**
   * The login spinner, for `RedirectPage`: it turns this on before handing the browser to
   * the identity provider, so the app does not flash a logged-out screen on the way out.
   *
   * It used to assign the field directly from `componentDidMount` — an observed observable
   * written outside an action. See docs/mobx-6-class-d.md.
   */
  @action
  public setCurrentUserFetching(value: boolean) {
    this.isCurrentUserFetching = value;
  }

  /**
   * Exchanges a Feide authorization code, at most once per code.
   *
   * StrictMode mounts, unmounts and remounts every component on first render, so
   * FeideCodeGetter's componentDidMount fires twice and this used to POST the same
   * code twice, near simultaneously. Dataporten treats a replayed code as an attack
   * and revokes every token it issued from that code, so the second request did not
   * merely fail on its own — it killed the token the first request was still using,
   * and both logins died with it.
   *
   * The map is module scoped rather than an instance field because App builds a new
   * LoginStore inside render(), so an instance field would not survive a remount.
   * Entries are never evicted: a code is single use, so replaying a settled one must
   * return the original outcome rather than reach the network again.
   */
  public async getUserWithToken(code: string) {
    const started = codeExchanges.get(code);

    if (started !== undefined) {
      return started;
    }

    const exchange = this.exchangeCode(code);
    codeExchanges.set(code, exchange);

    return exchange;
  }

  public async getUserWithTokenFromLogPass(email: string, password: string) {
    runInAction(() => {
      this.isCurrentUserFetching = true;
    });

    const response = await this.userService.getUserWithTokenFromLogPass(email, password);
    if (response) {
      runInAction(() => {
        this.currentUser = this.storageInteractor.getUser();
      });
    }

    if (response !== 'login_wp') {
      runInAction(() => {
        this.isCurrentUserFetching = false;
      });
    }
  }

  @action
  public getCurrentUserType(): string {
    try {
      const currentUser = this.userService.getCurrentUser();

      if (currentUser instanceof Teacher) {
        return UserType.Teacher;
      }

      return UserType.Student;
    } catch (e) {
      throw new Error(`get current user ${messageOf(e)}`, { cause: e });
    }
  }

  @action
  public logOut() {
    this.userService.logOut();
  }

  public getPostID() {
    return this.userService.getPostID();
  }

  public removePostID() {
    this.userService.removePostID();
  }

  public getAssignmentReferralToken() {
    return this.userService.getAssignmentReferralToken();
  }

  public removeAssignmentRefferalToken() {
    this.userService.removeAssignmentReferralToken();
  }

  public getTeachingPathReferralToken() {
    return this.userService.getTeachingPathReferralToken();
  }

  public removeTeachingPathRefferalToken() {
    this.userService.removeTeachingPathReferralToken();
  }

  public getAssignmentId() {
    return this.userService.getAssignmentId();
  }

  public removeAssignmentId() {
    this.userService.removeAssignmentId();
  }

  public getTeachingPathId() {
    return this.userService.getTeachingPathId();
  }

  public removeTeachingPathId() {
    this.userService.removeTeachingPathId();
  }

  public async getLocaleData(locale?: Locales) {
    const currentLocale = this.storageInteractor.getCurrentLocale()!;
    const localeData = await this.wpService.getLocaleData(locale || currentLocale);

    // FIXME potential bug here if WP will respond with array that contain more than two items
    localeData.forEach((locale) => {
      if (locale.slug.split('_')[0] === 'pll') {
        this.storageInteractor.setTaxonomyLocaleId(locale.id);
      } else {
        this.storageInteractor.setArticlesLocaleId(locale.id);
      }
    });
  }

  public async getMaintenance_data() {
    return this.userService.getMaintenanceStatus();
  }

  public async getMenuData(locale: string) {
    return this.userService.getMenuData(locale);
  }

  /*
  public async getMaintenance_mode() {
    const response = await API.get(`${process.env.REACT_APP_WP_API_URL}/wp-json/maintenance/v1/status`);
    return response.data.maintenance_mode;
  }

  public async getMaintenance_msj() {
    const response = await API.get(`${process.env.REACT_APP_WP_API_URL}/wp-json/maintenance/v1/status`);
    return response.data.maintenance_msg;
  }

  public async getMaintenance_start_time() {
    const response = await API.get(`${process.env.REACT_APP_WP_API_URL}/wp-json/maintenance/v1/status`);
    return response.data.start_time;
  }

  public async getMaintenance_end_time() {
    const response = await API.get(`${process.env.REACT_APP_WP_API_URL}/wp-json/maintenance/v1/status`);
    return response.data.end_time;
  }*/
}
